/**
 * @fileoverview Songs Redux slice.
 * @summary Manages song search state (local MongoDB cache and MusicBrainz API).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/songsSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'

/**
 * @summary Song interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface Song {
  id: string
  title: string
  artist: string
  duration?: number
}

/**
 * @summary Search source type.
 */
type SearchSource = 'local' | 'musicbrainz' | null

/**
 * @summary Songs state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface SongsState {
  searchResults: Song[]
  searchQuery: string
  artistQuery: string
  titleQuery: string
  isSearching: boolean
  error: string | null
  searchSource: SearchSource
  /** Whether local results were found and more results could be fetched from MusicBrainz */
  canLoadMore: boolean
}

/**
 * @summary Initial songs state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: SongsState = {
  searchResults: [],
  searchQuery: '',
  artistQuery: '',
  titleQuery: '',
  isSearching: false,
  error: null,
  searchSource: null,
  canLoadMore: false,
}

/**
 * @summary Search params for songs API.
 */
interface SearchSongsParams {
  query?: string
  title?: string
  artist?: string
  limit?: number
}

/**
 * @summary Helper function to search songs locally (not a thunk, just a function).
 */
async function fetchLocalSongs(params: SearchSongsParams): Promise<Song[]> {
  const urlParams = new URLSearchParams()
  if (params.query?.trim()) urlParams.append('q', params.query.trim())
  if (params.title?.trim()) urlParams.append('title', params.title.trim())
  if (params.artist?.trim()) urlParams.append('artist', params.artist.trim())
  urlParams.append('limit', String(params.limit || 50))

  try {
    const response = await apiRequest<{ songs: Song[]; count: number; source: string }>({
      method: 'GET',
      url: `${API_ENDPOINTS.SONGS.SEARCH_LOCAL}?${urlParams.toString()}`,
    })

    if (response.success && response.data?.songs) {
      return response.data.songs
    }
    return []
  } catch {
    // If local search fails, return empty array (we'll fall back to MusicBrainz)
    return []
  }
}

/**
 * @summary Helper function to search songs on MusicBrainz (not a thunk, just a function).
 */
async function fetchMusicBrainzSongs(params: SearchSongsParams): Promise<Song[]> {
  const urlParams = new URLSearchParams()
  if (params.query?.trim()) urlParams.append('q', params.query.trim())
  if (params.title?.trim()) urlParams.append('title', params.title.trim())
  if (params.artist?.trim()) urlParams.append('artist', params.artist.trim())
  urlParams.append('limit', String(params.limit || 50))

  const response = await apiRequest<{ songs: Song[]; count: number }>({
    method: 'GET',
    url: `${API_ENDPOINTS.SONGS.SEARCH}?${urlParams.toString()}`,
  })

  if (response.success && response.data?.songs) {
    return response.data.songs
  }
  throw new Error(response.message || 'Failed to search songs')
}

/**
 * @summary Search songs - first local, then MusicBrainz if no local results.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const searchSongs = createAsyncThunk(
  'songs/searchSongs',
  async ({ query, title, artist, limit = 50 }: SearchSongsParams) => {
    // Need at least one search param
    if (!query?.trim() && !title?.trim() && !artist?.trim()) {
      return { songs: [], query: query || '', title: title || '', artist: artist || '', source: null as SearchSource }
    }

    // First, try local search
    const localSongs = await fetchLocalSongs({ query, title, artist, limit })

    // If local results found, return them
    if (localSongs.length > 0) {
      return {
        songs: localSongs,
        query: query || '',
        title: title || '',
        artist: artist || '',
        source: 'local' as SearchSource,
      }
    }

    // No local results, search MusicBrainz directly
    const mbSongs = await fetchMusicBrainzSongs({ query, title, artist, limit })
    return {
      songs: mbSongs,
      query: query || '',
      title: title || '',
      artist: artist || '',
      source: 'musicbrainz' as SearchSource,
    }
  },
)

/**
 * @summary Search songs on MusicBrainz only (for "Load more" button).
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const searchSongsMusicBrainz = createAsyncThunk(
  'songs/searchSongsMusicBrainz',
  async ({ query, title, artist, limit = 50 }: SearchSongsParams) => {
    // Need at least one search param
    if (!query?.trim() && !title?.trim() && !artist?.trim()) {
      return { songs: [], query: query || '', title: title || '', artist: artist || '', source: 'musicbrainz' as SearchSource }
    }

    const songs = await fetchMusicBrainzSongs({ query, title, artist, limit })
    return {
      songs,
      query: query || '',
      title: title || '',
      artist: artist || '',
      source: 'musicbrainz' as SearchSource,
    }
  },
)

/**
 * @summary Add a custom song to the local cache (when search returns no results).
 * @param {{ title: string; artist: string }} payload - Title and artist.
 * @returns {Promise<Song>} The created song (with generated id).
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const addCustomSong = createAsyncThunk(
  'songs/addCustomSong',
  async ({ title, artist }: { title: string; artist: string }) => {
    const response = await apiRequest<{ song: Song }>({
      method: 'POST',
      url: API_ENDPOINTS.SONGS.ADD,
      data: { title: title.trim(), artist: artist.trim() },
    })

    if (!response.success || !response.data?.song) {
      throw new Error(response.message || 'Failed to add song')
    }

    return response.data.song
  },
)

/**
 * @summary Get song by ID.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const getSongById = createAsyncThunk('songs/getSongById', async (songId: string) => {
  const response = await apiRequest<{ song: Song }>({
    method: 'GET',
    url: API_ENDPOINTS.SONGS.GET(songId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to get song')
  }

  return response.data!.song
})

/**
 * @summary Songs slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const songsSlice = createSlice({
  name: 'songs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSearchResults: (state) => {
      state.searchResults = []
      state.searchQuery = ''
      state.artistQuery = ''
      state.titleQuery = ''
      state.error = null
      state.searchSource = null
      state.canLoadMore = false
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
  },
  extraReducers: (builder) => {
    // Search songs (local first, then MusicBrainz)
    builder
      .addCase(searchSongs.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchSongs.fulfilled, (state, action) => {
        state.isSearching = false
        state.searchResults = action.payload.songs
        state.searchQuery = action.payload.query
        state.artistQuery = action.payload.artist
        state.titleQuery = action.payload.title
        state.searchSource = action.payload.source
        // If source is local and we have results, can load more from MusicBrainz
        state.canLoadMore = action.payload.source === 'local' && action.payload.songs.length > 0
        state.error = null
      })
      .addCase(searchSongs.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search songs'
      })

    // Search songs MusicBrainz only (appends to existing results)
    builder
      .addCase(searchSongsMusicBrainz.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchSongsMusicBrainz.fulfilled, (state, action) => {
        state.isSearching = false
        // Get existing song IDs to avoid duplicates
        const existingIds = new Set(state.searchResults.map((s) => s.id))
        // Append new songs that are not already in results
        const newSongs = action.payload.songs.filter((s) => !existingIds.has(s.id))
        state.searchResults = [...state.searchResults, ...newSongs]
        state.searchQuery = action.payload.query
        state.artistQuery = action.payload.artist
        state.titleQuery = action.payload.title
        state.searchSource = 'musicbrainz'
        // No more results to load after MusicBrainz
        state.canLoadMore = false
        state.error = null
      })
      .addCase(searchSongsMusicBrainz.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search songs'
      })
  },
})

export const { clearError, clearSearchResults, setSearchQuery } = songsSlice.actions
export default songsSlice.reducer
